<template>
  <div class="container-fluid mt-4">
    <div class="d-flex justify-content-between align-items-center mb-5">
      <h2 class="fw-bold text-dark mb-0">Restaurants <span class="text-muted fs-5">({{ restaurants.length }})</span></h2>
      <div class="d-flex align-items-center gap-3">
        <b-form-input v-model="searchTerm" placeholder="Search restaurants..." class="form-control bg-white shadow-sm border-0 rounded-pill px-4" style="width: 300px;"></b-form-input>
        <button class="btn btn-primary-gradient px-4 rounded-pill shadow-sm" @click="openAddModal">
          <i class="fa fa-plus me-2"></i> Add Restaurant
        </button>
      </div>
    </div>

    <div class="glass-panel overflow-hidden">
      <b-table-simple striped hover class="mb-0">
        <b-thead class="bg-light">
          <b-tr>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Name</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Opening Hours</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Website</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Address</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Pick Up Locations</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4 text-end">Actions</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="restaurant in filteredRestaurants" :key="restaurant.id" class="align-middle">
            <b-td class="px-4 fw-bold text-dark">
              {{ restaurant.attributes.name }}
            </b-td>
            <b-td class="px-4 text-muted">{{ restaurant.attributes.opening_hours || 'N/A' }}</b-td>
            <b-td class="px-4">
              <a v-if="restaurant.attributes.website" :href="restaurant.attributes.website" target="_blank" class="text-primary text-decoration-none">
                Visit
              </a>
              <span v-else class="text-muted">N/A</span>
            </b-td>
            <b-td class="px-4 text-muted">{{ restaurant.attributes.address || 'N/A' }}</b-td>
            <b-td class="px-4">
              <span v-if="restaurant.attributes.locations?.data?.length > 0" class="text-dark">
                {{ restaurant.attributes.locations.data.map(l => l.attributes.name).join(', ') }}
              </span>
              <span v-else class="text-muted">N/A</span>
            </b-td>
            <b-td class="px-4 text-end">
              <button class="btn btn-light btn-sm rounded-pill text-primary px-3 fw-bold me-2" @click="editRestaurant(restaurant)">
                <i class="fa fa-pen me-1"></i> Edit
              </button>
              <button class="btn btn-light btn-sm rounded-pill text-danger px-3 fw-bold" @click="promptDelete(restaurant.id)">
                <i class="fa fa-trash me-1"></i> Delete
              </button>
            </b-td>
          </b-tr>
          <b-tr v-if="filteredRestaurants.length === 0">
            <b-td colspan="6" class="text-center py-5 text-muted">No restaurants found.</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <!-- Add/Edit Modal -->
    <b-modal id="restaurant-modal" :title="isEditing ? 'Edit Restaurant' : 'Add Restaurant'" hide-footer centered header-class="border-0 pb-0" body-class="p-4" content-class="border-0 shadow-lg rounded-4">
      <form @submit.prevent="handleSubmit">
        <div class="mb-3">
          <label class="form-label text-muted small fw-bold mb-2">Name</label>
          <input type="text" v-model="form.name" class="form-control bg-light border-0" required placeholder="e.g. Bella Italia" />
        </div>
        <div class="mb-3">
          <label class="form-label text-muted small fw-bold mb-2">Address</label>
          <input type="text" v-model="form.address" class="form-control bg-light border-0" required placeholder="Full address" />
        </div>
        <div class="row">
          <div class="col-6 mb-3">
            <label class="form-label text-muted small fw-bold mb-2">Opening Hours</label>
            <input type="text" v-model="form.opening_hours" class="form-control bg-light border-0" placeholder="e.g. 10:00 - 22:00" />
          </div>
          <div class="col-6 mb-3">
            <label class="form-label text-muted small fw-bold mb-2">Website</label>
            <input type="url" v-model="form.website" class="form-control bg-light border-0" placeholder="https://..." />
          </div>
        </div>
        <div class="row">
          <div class="col-6 mb-4">
            <label class="form-label text-muted small fw-bold mb-2">Latitude</label>
            <input type="text" v-model="form.latitude" class="form-control bg-light border-0" required placeholder="e.g. 52.5200" />
          </div>
          <div class="col-6 mb-4">
            <label class="form-label text-muted small fw-bold mb-2">Longitude</label>
            <input type="text" v-model="form.longitude" class="form-control bg-light border-0" required placeholder="e.g. 13.4050" />
          </div>
        </div>
        <div class="mb-4">
          <label class="form-label text-muted small fw-bold mb-2">Pick Up Location</label>
          <b-form-select v-model="form.location" :options="locationOptions" class="form-control bg-light border-0">
            <template #first>
              <option :value="null" disabled>Select location...</option>
            </template>
          </b-form-select>
        </div>
        <div class="d-flex justify-content-end mt-2">
          <button type="button" class="btn btn-light rounded-pill px-4 me-3 fw-bold" @click="$bvModal.hide('restaurant-modal')">Cancel</button>
          <button type="submit" class="btn btn-primary-gradient rounded-pill px-4 shadow-sm" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ isEditing ? 'Save Changes' : 'Create Restaurant' }}
          </button>
        </div>
      </form>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal id="delete-confirm-modal" title="Confirm Deletion" hide-footer centered header-class="border-0 pb-0" body-class="p-4 text-center" content-class="border-0 shadow-lg rounded-4">
      <div class="mb-4">
        <i class="fa fa-exclamation-triangle text-danger mb-3" style="font-size: 3rem;"></i>
        <h4 class="fw-bold text-dark mb-2">Delete Restaurant?</h4>
        <p class="text-muted">This action cannot be undone. Are you sure you want to permanently delete this restaurant?</p>
      </div>
      <div class="d-flex justify-content-center gap-3 mt-4">
        <button type="button" class="btn btn-light rounded-pill px-4 fw-bold" @click="$bvModal.hide('delete-confirm-modal')">Cancel</button>
        <button type="button" class="btn btn-danger rounded-pill px-4 fw-bold shadow-sm" @click="confirmDelete" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Yes, Delete
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      restaurants: [],
      locationOptions: [],
      searchTerm: '',
      isEditing: false,
      loading: false,
      restaurantToDelete: null,
      form: {
        id: null,
        name: '',
        opening_hours: '',
        website: '',
        latitude: '',
        longitude: '',
        address: '',
        location: null
      }
    };
  },
  async mounted() {
    await Promise.all([this.getData(), this.getLocations()]);
  },
  computed: {
    filteredRestaurants() {
      if (!this.searchTerm) return this.restaurants;
      const term = this.searchTerm.toLowerCase();
      return this.restaurants.filter(r => 
        (r.attributes.name && r.attributes.name.toLowerCase().includes(term)) ||
        (r.attributes.address && r.attributes.address.toLowerCase().includes(term)) ||
        (r.attributes.website && r.attributes.website.toLowerCase().includes(term))
      );
    }
  },
  methods: {
    async getData() {
      try {
        const result = await this.$xhr.get("/api/restaurants?populate=*");
        this.restaurants = result.data.data;
      } catch (error) {
        console.error("Error fetching restaurants", error);
      }
    },
    async getLocations() {
      try {
        const result = await this.$xhr.get("/api/sk-locations");
        this.locationOptions = result.data.data.map(loc => ({
          value: loc.id,
          text: loc.attributes.name
        }));
      } catch (error) {
        console.error("Error fetching locations", error);
      }
    },
    openAddModal() {
      this.isEditing = false;
      this.form = { id: null, name: '', opening_hours: '', website: '', latitude: '', longitude: '', address: '', location: null };
      this.$bvModal.show('restaurant-modal');
    },
    editRestaurant(restaurant) {
      this.isEditing = true;
      this.form = {
        id: restaurant.id,
        name: restaurant.attributes.name,
        opening_hours: restaurant.attributes.opening_hours,
        website: restaurant.attributes.website,
        latitude: restaurant.attributes.latitude,
        longitude: restaurant.attributes.longitude,
        address: restaurant.attributes.address,
        location: restaurant.attributes.locations?.data?.[0]?.id || null
      };
      this.$bvModal.show('restaurant-modal');
    },
    async handleSubmit() {
      this.loading = true;
      try {
        const payload = { data: { 
          name: this.form.name,
          opening_hours: this.form.opening_hours,
          website: this.form.website,
          latitude: this.form.latitude, 
          longitude: this.form.longitude, 
          address: this.form.address,
          locations: this.form.location ? [this.form.location] : []
        }};

        if (this.isEditing) {
          await this.$xhr.put(`/api/restaurants/${this.form.id}`, payload);
          this.$bvToast.toast('Restaurant updated!', { title: 'Success', variant: 'success', solid: true });
        } else {
          await this.$xhr.post("/api/restaurants", payload);
          this.$bvToast.toast('Restaurant created!', { title: 'Success', variant: 'success', solid: true });
        }
        await this.getData();
        this.$bvModal.hide('restaurant-modal');
      } catch (error) {
        console.error(error);
        this.$bvToast.toast('Operation failed', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },
    promptDelete(id) {
      this.restaurantToDelete = id;
      this.$bvModal.show('delete-confirm-modal');
    },
    async confirmDelete() {
      if (!this.restaurantToDelete) return;
      this.loading = true;
      try {
        await this.$xhr.delete(`/api/restaurants/${this.restaurantToDelete}`);
        this.restaurants = this.restaurants.filter(r => r.id !== this.restaurantToDelete);
        this.$bvToast.toast('Restaurant deleted', { title: 'Deleted', variant: 'warning', solid: true });
        this.$bvModal.hide('delete-confirm-modal');
      } catch (error) {
        console.error(error);
        this.$bvToast.toast('Delete failed', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
        this.restaurantToDelete = null;
      }
    }
  }
};
</script>

<style scoped>
.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.08) !important;
}
.btn-light {
  background-color: #F8F9FC;
  border: 1px solid #E2E2EA;
}
.btn-light:hover {
  background-color: #E2E2EA;
}
.rounded-4 {
  border-radius: 20px !important;
}
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
